import { Injectable, Pipe, PipeTransform } from '@angular/core';
import { Device, DeviceKind, toDeviceKind } from '@app/core/interfaces/c5/device';
import { loggerFor } from '@app/utils/logger';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
@Pipe({
  name: 'deviceName',
  // make it inpure so that it reacts also on application language change
  pure: false,
  standalone: false,
})
export class DeviceNamePipe implements PipeTransform {
  private logger = loggerFor('DeviceNamePipe');

  constructor(private translateService: TranslateService) {}

  private pretty(value: string): string {
    return value.split('@')[0];
  }

  /**
   * Extracts device name from the device data based on calculated device kind.
   *
   * `displayName` can additionally be provided in the model data
   * and plays a role in determining the exact name of an IAD device.
   * That name in turn should be extracted from DeviceSpec data.
   * E.g. this is done in end-user devices listing page.
   * @param device
   */
  public transform(device: Device & { displayName?: string }): string {
    let deviceKind = toDeviceKind(device);

    if (deviceKind === DeviceKind.MOBILE) {
      return this.pretty(device.name);
    } else if (deviceKind === DeviceKind.IAD) {
      let line = `${this.translateService.instant('device.iad.line')} ${device.line}`;
      let mac;
      let name;

      if (device.displayName) {
        name = device.displayName;
      } else {
        name = this.translateService.instant('device.kind.IAD');
      }

      // in case device was not fetched deep then there is no phone data
      if (device.phone?.mac) {
        mac = `(${device.phone.mac})`;
      } else {
        const split = device.name.split(':');
        mac = split[1]?.replace('Line', '').trim() || '';
        mac = `(${mac})`;
      }

      return `${name} ${mac}, ${line}`;
    } else if (deviceKind === DeviceKind.PHONE) {
      return this.pretty(device.name);
    } else if (deviceKind === DeviceKind.MS_TEAMS) {
      return this.pretty(device.name);
    } else if (deviceKind === DeviceKind.EXTERNAL_PBX) {
      return this.pretty(device.name);
    } else {
      return this.translateService.instant('device.kind.' + deviceKind);
    }

    this.logger.warn('Device name could not be extracted from the device data', device);

    return this.translateService.instant('device.unknown');
  }
}
